"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Check,
  CheckCircle2,
  CircleAlert,
  FileCheck2,
  Loader2,
  Save,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import type { Project, Team, User } from "@/lib/types";
import { Drawer } from "@/components/ui/Drawer";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

type ReviewStatus = "pending" | "accepted" | "rejected" | "applied";

interface Proposal {
  id: string;
  batch_id: string;
  workspace_id: string;
  position: number;
  title: string;
  description: string;
  team_id: string | null;
  project_id: string | null;
  owner_id: string | null;
  executor_agent_id: string | null;
  requester_id: string | null;
  priority: "none" | "urgent" | "high" | "medium" | "low";
  due_date: string | null;
  status: ReviewStatus;
  source_excerpt: string;
  confidence: number | null;
  created_issue_id: string | null;
}

interface Batch {
  id: string;
  status: "draft" | "applied" | "dismissed";
  idempotency_key: string | null;
  created_at: string;
  applied_at: string | null;
}

interface TaskProposalReviewProps {
  workspaceId: string;
  docId: string;
  docTitle: string;
  teams: Team[];
  projects: Project[];
  users: User[];
  onClose: () => void;
}

export function TaskProposalReview({
  workspaceId,
  docId,
  docTitle,
  teams,
  projects,
  users,
  onClose,
}: TaskProposalReviewProps) {
  const workspaceSlug = useStore((state) => state.workspaces.find((workspace) => workspace.id === workspaceId)?.slug);
  const visibleIssues = useStore((state) => state.issues);
  const selectIssue = useStore((state) => state.selectIssue);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [createdCount, setCreatedCount] = useState<number | null>(null);
  const refreshWorkspaceLiveData = useStore(
    (state) => state.refreshWorkspaceLiveData
  );

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `/api/workspaces/${encodeURIComponent(workspaceId)}/task-proposal-batches`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ source_type: "doc", source_id: docId }),
            signal: controller.signal,
          }
        );
        const payload = (await response.json()) as {
          batch?: Batch;
          proposals?: Proposal[];
          error?: string;
          message?: string;
        };
        if (!response.ok) {
          throw new Error(
            payload.message || humanizeError(payload.error || "generation_failed")
          );
        }
        setBatch(payload.batch ?? null);
        setProposals(payload.proposals ?? []);
        if (payload.batch?.status === "applied") {
          setCreatedCount(
            (payload.proposals ?? []).filter(
              (proposal) => proposal.created_issue_id
            ).length
          );
        }
      } catch (caught) {
        if ((caught as Error).name !== "AbortError") {
          setError((caught as Error).message);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [docId, workspaceId]);

  const humanUsers = useMemo(
    () => users.filter((user) => !user.isBot),
    [users]
  );
  const agentUsers = useMemo(
    () => users.filter((user) => user.isBot),
    [users]
  );
  const pendingCount = proposals.filter(
    (proposal) => proposal.status === "pending"
  ).length;
  const acceptedCount = proposals.filter(
    (proposal) => proposal.status === "accepted"
  ).length;
  const rejectedCount = proposals.filter(
    (proposal) => proposal.status === "rejected"
  ).length;
  const completedCreatedCount =
    createdCount ??
    proposals.filter((proposal) => proposal.created_issue_id).length;

  function changeProposal(id: string, patch: Partial<Proposal>) {
    setProposals((current) =>
      current.map((proposal) =>
        proposal.id === id ? { ...proposal, ...patch } : proposal
      )
    );
    setDirty((current) => new Set(current).add(id));
    setError("");
  }

  async function persistProposal(
    proposal: Proposal,
    override: Partial<Proposal> = {}
  ) {
    if (!batch) return false;
    const next = { ...proposal, ...override };
    if (!next.title.trim()) {
      setError("Add a task title before saving this proposal.");
      return false;
    }
    if (next.status === "accepted" && !next.team_id) {
      setError(`Choose a team before accepting “${next.title}”.`);
      return false;
    }
    setSaving((current) => new Set(current).add(proposal.id));
    setError("");
    try {
      const response = await fetch(
        `/api/workspaces/${encodeURIComponent(workspaceId)}/task-proposal-batches/${encodeURIComponent(batch.id)}/items/${encodeURIComponent(proposal.id)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            title: next.title,
            description: next.description,
            team_id: next.team_id,
            project_id: next.project_id,
            owner_id: next.owner_id,
            executor_agent_id: next.executor_agent_id,
            priority: next.priority,
            due_date: next.due_date,
            status: next.status,
          }),
        }
      );
      const payload = (await response.json()) as {
        proposal?: Proposal;
        error?: string;
      };
      if (!response.ok || !payload.proposal) {
        throw new Error(humanizeError(payload.error || "update_failed"));
      }
      setProposals((current) =>
        current.map((item) =>
          item.id === proposal.id ? payload.proposal! : item
        )
      );
      setDirty((current) => {
        const nextDirty = new Set(current);
        nextDirty.delete(proposal.id);
        return nextDirty;
      });
      return true;
    } catch (caught) {
      setError((caught as Error).message);
      return false;
    } finally {
      setSaving((current) => {
        const nextSaving = new Set(current);
        nextSaving.delete(proposal.id);
        return nextSaving;
      });
    }
  }

  async function setReviewStatus(
    proposal: Proposal,
    status: "accepted" | "rejected"
  ) {
    changeProposal(proposal.id, { status });
    await persistProposal(proposal, { status });
  }

  async function apply() {
    if (!batch || pendingCount || dirty.size) return;
    setApplying(true);
    setError("");
    try {
      const response = await fetch(
        `/api/workspaces/${encodeURIComponent(workspaceId)}/task-proposal-batches/${encodeURIComponent(batch.id)}/apply`,
        { method: "POST" }
      );
      const payload = (await response.json()) as {
        batch?: Batch;
        proposals?: Proposal[];
        created?: Array<{ out_issue_id: string }>;
        error?: string;
      };
      if (!response.ok) {
        throw new Error(humanizeError(payload.error || "apply_failed"));
      }
      if (payload.batch) setBatch(payload.batch);
      if (payload.proposals) setProposals(payload.proposals);
      setCreatedCount(payload.created?.length ?? acceptedCount);
      await refreshWorkspaceLiveData(workspaceId);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setApplying(false);
    }
  }

  async function dismiss() {
    if (!batch || applying || saving.size > 0) return;
    if (
      !window.confirm(
        "Dismiss this proposal batch? You will need to change the source doc before proposing this exact revision again."
      )
    ) {
      return;
    }
    setApplying(true);
    setError("");
    try {
      const response = await fetch(
        `/api/workspaces/${encodeURIComponent(workspaceId)}/task-proposal-batches/${encodeURIComponent(batch.id)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ action: "dismiss" }),
        }
      );
      const payload = (await response.json()) as {
        batch?: Batch;
        error?: string;
      };
      if (!response.ok) {
        throw new Error(humanizeError(payload.error || "dismiss_failed"));
      }
      if (payload.batch) setBatch(payload.batch);
      onClose();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setApplying(false);
    }
  }

  function requestClose() {
    if (applying || saving.size > 0) return;
    if (
      dirty.size > 0 &&
      !window.confirm(
        `Discard unsaved edits to ${dirty.size} proposed task${
          dirty.size === 1 ? "" : "s"
        }?`
      )
    ) {
      return;
    }
    onClose();
  }

  return (
    <Drawer
      open
      onClose={requestClose}
      ariaLabelledBy="proposal-review-title"
      width={720}
      closeDisabled={applying || saving.size > 0}
    >
      <div
        className="flex min-h-0 flex-1 flex-col"
        aria-busy={loading || applying || saving.size > 0}
      >
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-3">
          <Sparkles size={14} className="text-[var(--accent)]" />
          <div className="min-w-0 flex-1">
            <h2
              id="proposal-review-title"
              className="truncate text-[13.5px] font-medium tracking-tight"
            >
              Review proposed tasks
            </h2>
            <p className="truncate text-[11.5px] text-[var(--fg-tertiary)]">
              {docTitle} · nothing is created until you apply
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={applying || saving.size > 0}
            className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] disabled:opacity-40"
            aria-label="Close task proposal review"
          >
            <X size={14} />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center text-[12px] text-[var(--fg-tertiary)]">
            <Loader2 size={14} className="mr-2 animate-spin" />
            Finding explicit commitments…
          </div>
        ) : error && !batch ? (
          <div
            role="alert"
            className="flex flex-1 flex-col items-center justify-center px-8 text-center"
          >
            <CircleAlert size={20} className="mb-3 text-[var(--orange)]" />
            <p className="max-w-md text-[12.5px] leading-5 text-[var(--fg-secondary)]">
              {error}
            </p>
            <button
              type="button"
              onClick={requestClose}
              className="mt-4 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-[var(--bg-hover)]"
            >
              Close
            </button>
          </div>
        ) : batch?.status === "dismissed" ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
            <XCircle size={22} className="mb-3 text-[var(--fg-tertiary)]" />
            <h3 className="text-[15.5px] font-medium tracking-tight">
              Review dismissed
            </h3>
            <p className="mt-1 max-w-md text-[12.5px] text-[var(--fg-tertiary)]">
              This exact doc revision was already dismissed. Update the source
              actions before proposing a fresh batch.
            </p>
            <button
              type="button"
              onClick={requestClose}
              className="mt-4 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-[var(--bg-hover)]"
            >
              Close
            </button>
          </div>
        ) : createdCount !== null || batch?.status === "applied" ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[var(--green)] bg-[var(--bg-secondary)] text-[var(--green)]">
              <CheckCircle2 size={22} />
            </div>
            <h3 className="text-[15.5px] font-medium tracking-tight">
              Review complete
            </h3>
            <p className="mt-1 text-[12.5px] text-[var(--fg-tertiary)]">
              {completedCreatedCount} task
              {completedCreatedCount === 1 ? "" : "s"} created. Rejected
              proposals were kept as review history.
            </p>
            {workspaceSlug && <ul className="mt-3 w-full max-w-md space-y-1">
              {proposals.filter((proposal) => proposal.workspace_id === workspaceId && proposal.created_issue_id && /^[0-9a-f-]{36}$/i.test(proposal.created_issue_id)).map((proposal) => {
                const task = visibleIssues.find((issue) => issue.workspaceId === workspaceId && issue.id === proposal.created_issue_id);
                return <li key={proposal.id}><a
                  href={`/${encodeURIComponent(workspaceSlug)}?issue=${encodeURIComponent(proposal.created_issue_id!)}`}
                  className="focus-ring flex min-h-11 items-center justify-center rounded-md px-3 text-[12px] text-[var(--accent)]"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                    event.preventDefault(); onClose(); selectIssue(proposal.created_issue_id);
                  }}
                >{task ? `Abrir ${task.identifier}` : "Abrir tarefa criada desta fonte"}</a></li>;
              })}
            </ul>}
            <button
              type="button"
              onClick={requestClose}
              className="mt-4 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)]"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div
              aria-live="polite"
              className="flex items-center gap-3 border-b border-[var(--border)] px-4 py-2 text-[11.5px] text-[var(--fg-tertiary)]"
            >
              <span>{proposals.length} proposed</span>
              <span className="text-[var(--green)]">{acceptedCount} accepted</span>
              <span>{rejectedCount} rejected</span>
              <span>{pendingCount} to review</span>
              <span className="ml-auto">Edit before accepting</span>
            </div>
            <div className="scrollbar-thin flex-1 space-y-3 overflow-y-auto p-4">
              {proposals.map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  teams={teams}
                  projects={projects}
                  humans={humanUsers}
                  agents={agentUsers}
                  dirty={dirty.has(proposal.id)}
                  saving={saving.has(proposal.id)}
                  onChange={(patch) => changeProposal(proposal.id, patch)}
                  onSave={() => void persistProposal(proposal)}
                  onAccept={() => void setReviewStatus(proposal, "accepted")}
                  onReject={() => void setReviewStatus(proposal, "rejected")}
                />
              ))}
            </div>
            <div className="border-t border-[var(--border)] px-4 py-3">
              {error && (
                <div
                  role="alert"
                  className="mb-2 rounded-md border border-[var(--red)] bg-[var(--bg-secondary)] px-3 py-2 text-[11.5px] text-[var(--red)]"
                >
                  {error}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void dismiss()}
                  disabled={applying || saving.size > 0}
                  className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[12px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-40"
                >
                  Dismiss batch
                </button>
                <p className="hidden flex-1 text-right text-[11px] text-[var(--fg-muted)] sm:block">
                  {pendingCount
                    ? `Review ${pendingCount} remaining proposal${pendingCount === 1 ? "" : "s"}`
                    : dirty.size
                      ? "Save edits before applying"
                      : "Only accepted proposals become tasks"}
                </p>
                <button
                  type="button"
                  onClick={() => void apply()}
                  disabled={
                    applying ||
                    saving.size > 0 ||
                    pendingCount > 0 ||
                    dirty.size > 0
                  }
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {applying ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <FileCheck2 size={13} />
                  )}
                  {acceptedCount
                    ? `Create ${acceptedCount} task${acceptedCount === 1 ? "" : "s"}`
                    : "Finish review"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

function ProposalCard({
  proposal,
  teams,
  projects,
  humans,
  agents,
  dirty,
  saving,
  onChange,
  onSave,
  onAccept,
  onReject,
}: {
  proposal: Proposal;
  teams: Team[];
  projects: Project[];
  humans: User[];
  agents: User[];
  dirty: boolean;
  saving: boolean;
  onChange: (patch: Partial<Proposal>) => void;
  onSave: () => void;
  onAccept: () => void;
  onReject: () => void;
}) {
  const disabled = proposal.status === "applied";
  const editingDisabled = disabled || saving;
  const compatibleProjects = proposal.team_id
    ? projects.filter(
        (project) =>
          project.teamIds.length === 0 ||
          project.teamIds.includes(proposal.team_id!)
      )
    : projects;

  function changeTeam(teamId: string | null) {
    const selectedProject = projects.find(
      (project) => project.id === proposal.project_id
    );
    const projectRemainsCompatible =
      !teamId ||
      !selectedProject ||
      selectedProject.teamIds.length === 0 ||
      selectedProject.teamIds.includes(teamId);
    onChange({
      team_id: teamId,
      project_id: projectRemainsCompatible ? proposal.project_id : null,
    });
  }

  return (
    <article
      className={cn(
        "rounded-md border bg-[var(--bg-secondary)] p-3 transition-colors",
        proposal.status === "accepted"
          ? "border-[var(--green)]"
          : proposal.status === "rejected"
            ? "border-[var(--border)] opacity-70"
            : "border-[var(--border)] hover:border-[var(--border-strong)]"
      )}
    >
      <div className="flex items-start gap-2">
        <span className="mt-1 text-[10.5px] tabular-nums text-[var(--fg-muted)]">
          {proposal.position + 1}
        </span>
        <input
          aria-label={`Proposed task ${proposal.position + 1} title`}
          value={proposal.title}
          maxLength={280}
          disabled={editingDisabled}
          onChange={(event) => onChange({ title: event.target.value })}
          className="min-w-0 flex-1 border-b border-transparent bg-transparent pb-1 text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)] disabled:cursor-wait disabled:opacity-50"
        />
        {dirty && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            aria-label={`Save proposed task ${proposal.position + 1}`}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-[var(--border)] px-2 text-[11px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-40"
          >
            {saving ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Save size={11} />
            )}
            Save
          </button>
        )}
      </div>

      <blockquote className="mt-2 border-l-2 border-[var(--border-strong)] pl-2 text-[11px] leading-4 text-[var(--fg-tertiary)]">
        {proposal.source_excerpt}
      </blockquote>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Team · required">
          <select
            value={proposal.team_id ?? ""}
            disabled={editingDisabled}
            onChange={(event) => changeTeam(event.target.value || null)}
            className={SELECT_CLS}
          >
            <option value="">Choose team…</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Project">
          <select
            value={proposal.project_id ?? ""}
            disabled={editingDisabled}
            onChange={(event) =>
              onChange({ project_id: event.target.value || null })
            }
            className={SELECT_CLS}
          >
            <option value="">No project</option>
            {compatibleProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Human owner">
          <select
            value={proposal.owner_id ?? ""}
            disabled={editingDisabled}
            onChange={(event) =>
              onChange({ owner_id: event.target.value || null })
            }
            className={SELECT_CLS}
          >
            <option value="">Unassigned</option>
            {humans.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Agent executor">
          <select
            value={proposal.executor_agent_id ?? ""}
            disabled={editingDisabled}
            onChange={(event) =>
              onChange({ executor_agent_id: event.target.value || null })
            }
            className={SELECT_CLS}
          >
            <option value="">No agent</option>
            {agents.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select
            value={proposal.priority}
            disabled={editingDisabled}
            onChange={(event) =>
              onChange({
                priority: event.target.value as Proposal["priority"],
              })
            }
            className={SELECT_CLS}
          >
            <option value="none">No priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Field>
        <Field label="Due date">
          <input
            type="date"
            value={proposal.due_date?.slice(0, 10) ?? ""}
            disabled={editingDisabled}
            onChange={(event) =>
              onChange({
                due_date: event.target.value
                  ? `${event.target.value}T12:00:00.000Z`
                  : null,
              })
            }
            className={SELECT_CLS}
          />
        </Field>
      </div>
      <label className="mt-2 block text-[10.5px] uppercase tracking-wider text-[var(--fg-muted)]">
        Task context
        <textarea
          value={proposal.description}
          rows={2}
          maxLength={20_000}
          disabled={editingDisabled}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder="Add the context the owner or agent will need…"
          className="scrollbar-thin mt-1 w-full resize-y rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1.5 text-[12px] normal-case tracking-normal text-[var(--fg-secondary)] outline-none transition-colors focus:border-[var(--accent)] disabled:cursor-wait disabled:opacity-50"
        />
      </label>

      <div className="mt-3 flex items-center gap-2 border-t border-[var(--border)] pt-3">
        <button
          type="button"
          onClick={onAccept}
          disabled={saving || disabled}
          aria-pressed={proposal.status === "accepted"}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] font-medium transition-colors disabled:opacity-40",
            proposal.status === "accepted"
              ? "border-[var(--green)] bg-[var(--bg-active)] text-[var(--green)]"
              : "border-[var(--border)] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
          )}
        >
          <Check size={12} />
          Accept
        </button>
        <button
          type="button"
          onClick={onReject}
          disabled={saving || disabled}
          aria-pressed={proposal.status === "rejected"}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] font-medium transition-colors disabled:opacity-40",
            proposal.status === "rejected"
              ? "border-[var(--red)] bg-[var(--bg-active)] text-[var(--red)]"
              : "border-[var(--border)] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
          )}
        >
          <XCircle size={12} />
          Reject
        </button>
        <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
          {proposal.confidence !== null
            ? `${Math.round(proposal.confidence * 100)}% extraction confidence`
            : "Review required"}
        </span>
      </div>
    </article>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="text-[10.5px] uppercase tracking-wider text-[var(--fg-muted)]">
      {label}
      {children}
    </label>
  );
}

const SELECT_CLS =
  "mt-1 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1.5 text-[11.5px] normal-case tracking-normal text-[var(--fg-secondary)] outline-none transition-colors focus:border-[var(--accent)] disabled:cursor-wait disabled:opacity-50";

function humanizeError(error: string): string {
  const messages: Record<string, string> = {
    no_explicit_commitments:
      "No explicit commitments found. Add a checkbox, TODO, Ação, an action-items section, or a clear “will” statement.",
    source_not_found: "This doc is no longer available.",
    source_too_large: "This doc is too large to review safely in one batch.",
    proposal_review_incomplete: "Review every proposal before applying.",
    accepted_proposal_requires_team:
      "Every accepted proposal needs a team so Pauta can assign its identifier.",
    batch_not_editable: "This proposal batch has already been completed.",
    proposal_not_editable: "This proposal can no longer be edited.",
    invalid_workspace_reference:
      "One selected person, agent, project, or team is not in this workspace.",
  };
  return messages[error] ?? "Something went wrong. Please try again.";
}
